// La conciliación abierta. Mantiene las secciones numeradas del original, que
// son la guía de lectura del papel de trabajo, en el mismo orden.
//
// A-02 desaparece acá de raíz: JSX escapa por defecto, así que el nombre de
// comercio que viene del PDF no puede inyectar HTML. No es disciplina, es que
// el framework no lo permite.
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, descargar } from '../api';
import { useSesion } from '../sesion';
import type { Conciliacion } from '../tipos';
import { SelectorAsiento } from '../componentes/SelectorAsiento';

const ESTADOS = [
  'Conciliado', 'Conciliado c/dif', 'Posible (revisar)', 'No conciliado',
  'USD pendiente registrar', 'USD a confirmar', 'Contabilizado en próximo mes',
];

/** Mismo criterio de color que RES_CLASS del original. */
function clase(estado: string): 'ok' | 'warn' | 'bad' | 'info' {
  if (estado.startsWith('Conciliado')) return estado.includes('dif') ? 'warn' : 'ok';
  if (estado === 'No conciliado') return 'bad';
  if (estado.startsWith('USD')) return 'info';
  return 'warn';
}

const pesos = (v: string | null) =>
  v == null ? '' : Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function VistaConciliacion() {
  const { id = '' } = useParams();
  const { puede, soloLectura } = useSesion();
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  // Qué consumo tiene el selector de asiento abierto.
  const [eligiendo, setEligiendo] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['conciliacion', id],
    queryFn: () => api.get<Conciliacion>(`/api/conciliaciones/${id}`),
  });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['conciliacion', id] });
    void qc.invalidateQueries({ queryKey: ['conciliaciones'] });
  };

  const editar = useMutation({
    mutationFn: (v: { consumoId: string; movimientoId?: string | null; estado?: string; nota?: string }) =>
      api.post<{ nroTilde: string; estado: string; diferencia: number; tipoCambio: number | null; aprendio: string | null }>(
        `/api/conciliaciones/${id}/vinculos`, v),
    onSuccess: (r) => {
      setError('');
      setEligiendo(null);
      // Vincular a mano ENSEÑA el alias: el mes que viene se concilia solo.
      // Decirlo hace visible que la app aprende, que era invisible en el original.
      setAviso(
        `${r.nroTilde}: ${r.estado}` +
        (r.diferencia ? ` · diferencia $ ${pesos(String(r.diferencia))}` : '') +
        (r.tipoCambio ? ` · tipo de cambio implícito ${r.tipoCambio}` : '') +
        (r.aprendio ? ` — aprendido: «${r.aprendio}» se va a conciliar solo en los próximos resúmenes.` : ''),
      );
      refrescar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo guardar.'),
  });

  const reconciliar = useMutation({
    mutationFn: () => api.post<{ conciliados: number; total: number; preservados: number }>(
      `/api/conciliaciones/${id}/reconciliar`),
    onSuccess: (r) => {
      setError('');
      setAviso(`Reconciliado: ${r.conciliados} de ${r.total} · ${r.preservados} preservado(s) de los que confirmaste a mano.`);
      refrescar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo reconciliar.'),
  });

  const cerrar = useMutation({
    mutationFn: () => api.post<{ tildes: number; sinResolver: number; arrastrados: number }>(
      `/api/conciliaciones/${id}/cerrar`),
    onSuccess: (r) => {
      setError('');
      setAviso(
        `Conciliación cerrada y firmada. ${r.tildes} movimientos conciliados` +
        (r.sinResolver ? `, ${r.sinResolver} sin resolver` : '') +
        (r.arrastrados ? `. Se arrastraron ${r.arrastrados} pendiente(s) al próximo resumen.` : '.'),
      );
      refrescar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'No se pudo cerrar.'),
  });

  if (q.isLoading) return <div className="cargando">Cargando la conciliación…</div>;
  if (q.error) return <div className="aviso bad">{(q.error as Error).message}</div>;
  const c = q.data!;
  const r = c.resumen;
  const cerrada = c.estado === 'cerrada';
  const sec = c.secciones;
  const editable = !cerrada && !soloLectura;

  const totCargos = (r?.cargos ?? []).reduce((a, g) => a + Number(g.importe), 0);
  const totArs = (r?.consumos ?? []).filter((x) => x.moneda === 'ARS').reduce((a, x) => a + Number(x.importe), 0);
  const cuadra = r?.saldoArs != null && Math.abs(totArs + totCargos - Number(r.saldoArs)) < 0.5;
  const porEstado = (p: (e: string) => boolean) => c.vinculos.filter((v) => p(v.estado)).length;

  // Agrupaciones N:1: un mismo N° de tilde compartido por varios consumos.
  const porTilde = new Map<string, number>();
  for (const v of c.vinculos) porTilde.set(v.nroTilde, (porTilde.get(v.nroTilde) ?? 0) + 1);

  // La tabla recorre los CONSUMOS DEL RESUMEN, no los vínculos.
  //
  // Un consumo que no concilió no tiene vínculo —reconciliar() lo borra— así
  // que iterando los vínculos desaparecían de la pantalla justamente los que
  // necesitan atención del operador. El original recorría RES.consumos.
  const vinculoDe = new Map(c.vinculos.map((v) => [v.consumoId, v]));
  const filas = (r?.consumos ?? []).map((x) => ({ consumo: x, vinculo: vinculoDe.get(x.id) }));

  return (
    <>
      <p style={{ margin: '10px 0' }}><Link to="/">← volver a las conciliaciones</Link></p>

      <div className="controls">
        <div>
          <div className="muted">Empresa / Tarjeta</div>
          <b style={{ color: 'var(--navy)' }}>
            {c.tarjeta.empresa.nombre} · {c.tarjeta.nombre} ({c.tarjeta.cuentaContable})
          </b>
          <div className="muted">
            {r?.banco ?? '—'} · resumen {r?.resumenNro ?? '—'} · cierre {r?.cierre ?? '—'} · período {c.periodo}
          </div>
        </div>
        <div className="spacer" />
        {editable && (
          <button className="btn-run" disabled={reconciliar.isPending}
                  onClick={() => reconciliar.mutate()}>
            {reconciliar.isPending ? 'Reconciliando…' : 'Volver a conciliar'}
          </button>
        )}
        <button className="btn-ok" onClick={() => void descargar(`/api/conciliaciones/${id}/excel`).catch((e) => setError(e.message))}>
          Descargar Excel
        </button>
        {editable && puede('supervisor') && (
          <button className="btn-ok" style={{ background: 'var(--navy)' }}
                  disabled={cerrar.isPending}
                  onClick={() => {
                    if (confirm(
                      'Al CERRAR la conciliación:\n\n' +
                      '· Se congela: no se pueden editar más los estados ni las notas.\n' +
                      '· Se fijan los N° de conciliación de forma definitiva.\n' +
                      '· Queda firmada con tu nombre y la fecha.\n\n' +
                      (cuadra ? '✓ El total cuadra contra el PDF.' : '✗ ATENCIÓN: el total NO coincide con el PDF.') +
                      '\n\n¿Cerrar la conciliación?')) cerrar.mutate();
                  }}>
            Cerrar y firmar
          </button>
        )}
      </div>

      {cerrada && (
        <div className="aviso ok">
          <b>CERRADA Y FIRMADA</b> por {c.cerradaPor?.nombre ?? '—'}
          {c.cerradaEl && ` el ${c.cerradaEl.slice(0, 10)}`}. No se puede editar.
          {puede('supervisor') && ' Un supervisor puede reabrirla explicando el motivo.'}
        </div>
      )}
      {soloLectura && <div className="aviso info">Tu rol es <b>auditor</b>: solo lectura.</div>}
      {error && <div className="aviso bad" role="alert">{error}</div>}
      {aviso && <div className="aviso ok" role="status">{aviso}</div>}

      {/* ---------------------------------------------------- 1 controles */}
      <h2 className="seccion">
        <span className="num">1</span>CONTROLES AUTOMÁTICOS
        <span className="muted">— ¿la conciliación está sana?</span>
      </h2>
      <div className={`aviso ${cuadra ? 'ok' : 'bad'}`} style={{ margin: 0 }}>
        {cuadra
          ? '✓ La suma de los consumos y cargos coincide con el saldo del resumen del emisor.'
          : `✗ La suma de consumos y cargos da $ ${pesos(String(totArs + totCargos))} y el resumen dice $ ${pesos(r?.saldoArs ?? null)}. Revisar antes de cerrar.`}
      </div>

      <div className="cards">
        <div className="card"><div className="n">{r?.consumos.length ?? 0}</div><div className="l">Consumos del resumen</div></div>
        <div className="card"><div className="n" style={{ color: 'var(--ok)' }}>{porEstado((e) => e === 'Conciliado')}</div><div className="l">Conciliados</div></div>
        <div className="card"><div className="n" style={{ color: 'var(--warn)' }}>{porEstado((e) => e.includes('dif') || e === 'Posible (revisar)')}</div><div className="l">Con diferencia o a revisar</div></div>
        <div className="card"><div className="n" style={{ color: 'var(--bad)' }}>{porEstado((e) => e === 'No conciliado')}</div><div className="l">No conciliados</div></div>
        <div className="card"><div className="n">{c.movimientos.length}</div><div className="l">Movimientos del mayor</div></div>
      </div>

      {/* ------------------------------------------------------- 2 cuadre */}
      <h2 className="seccion">
        <span className="num">2</span>PAPEL DE CONCILIACIÓN
        <span className="muted">— del total del resumen al saldo contable</span>
      </h2>
      <div className="kb">
        <table>
          <thead><tr><th>Concepto</th><th className="num">Pesos</th></tr></thead>
          <tbody>
            <tr><td>Consumos del resumen en pesos</td><td className="num">{pesos(String(totArs))}</td></tr>
            <tr><td>Cargos e impuestos del resumen</td><td className="num">{pesos(String(totCargos))}</td></tr>
            <tr style={{ fontWeight: 700, background: '#f0f3f9' }}>
              <td>Total del resumen del emisor</td><td className="num">{pesos(String(totArs + totCargos))}</td>
            </tr>
            <tr><td className="muted">Saldo que declara el resumen</td><td className="num muted">{pesos(r?.saldoArs ?? null)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------- 3 movimientos */}
      <h2 className="seccion">
        <span className="num">3</span>MOVIMIENTOS CONCILIADOS
        <span className="muted">— cada consumo y el asiento del mayor con el que se concilió</span>
      </h2>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>N°</th><th className="fecha">Fecha</th><th>Comercio (resumen)</th><th className="num">Importe</th>
              <th>Cuota</th><th>Estado</th><th>Respaldo en el mayor</th><th>Criterio / comentario</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 14 }}>
                No hay consumos en el resumen.
              </td></tr>
            )}
            {filas.map(({ consumo: x, vinculo: v }) => {
              const estado = v?.estado ?? 'No conciliado';
              const enGrupo = v ? (porTilde.get(v.nroTilde) ?? 0) > 1 : false;
              return (
                <tr key={x.id}>
                  <td>
                    {v
                      ? <>
                          <span className="cn">{v.nroTilde}</span>
                          {enGrupo && (
                            <span className="tag info" style={{ marginLeft: 4 }}
                                  title="Varios consumos conciliados contra el mismo asiento del mayor">
                              grupo de {porTilde.get(v.nroTilde)}
                            </span>
                          )}
                        </>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="fecha">{x.fecha}</td>
                  <td>{x.comercio}</td>
                  <td className="num">
                    {pesos(x.importe)} <span className="muted">{x.moneda}</span>
                  </td>
                  <td>{x.cuota ?? ''}</td>
                  <td>
                    {editable ? (
                      <select value={estado} style={{ fontSize: 12, padding: '3px 6px' }}
                              onChange={(e) => editar.mutate({
                                consumoId: x.id,
                                movimientoId: v?.movimientoId ?? null,
                                estado: e.target.value,
                              })}>
                        {ESTADOS.map((op) => <option key={op} value={op}>{op}</option>)}
                      </select>
                    ) : (
                      <span className={`tag ${clase(estado)}`}>{estado}</span>
                    )}
                    {v && v.origen !== 'automatico' && (
                      <span className="tag warn" style={{ marginLeft: 4 }}>{v.origen}</span>
                    )}
                  </td>
                  <td className="vinculo">
                    {v?.movimiento
                      ? <>{v.movimiento.fecha} · {v.movimiento.proveedor ?? v.movimiento.concepto}</>
                      : <span className="muted">—</span>}
                    {v?.diferencia && Number(v.diferencia) !== 0 && (
                      <div className="muted">dif $ {pesos(v.diferencia)}</div>
                    )}
                    {editable && (
                      <button className="btn-ghost"
                              style={{ fontSize: 10, padding: '2px 6px', marginLeft: 6 }}
                              onClick={() => setEligiendo(eligiendo === x.id ? null : x.id)}>
                        {v?.movimiento ? 'cambiar' : 'vincular'}
                      </button>
                    )}
                    {eligiendo === x.id && (
                      <SelectorAsiento
                        consumo={x}
                        movimientos={c.movimientos}
                        vinculos={c.vinculos}
                        vinculoActual={v}
                        guardando={editar.isPending}
                        onCancelar={() => setEligiendo(null)}
                        onElegir={(movimientoId) =>
                          editar.mutate({ consumoId: x.id, movimientoId })}
                      />
                    )}
                  </td>
                  <td className="nota">
                    {editable ? (
                      <input type="text" defaultValue={v?.nota ?? ''} placeholder="—"
                             style={{ fontSize: 12, padding: '4px 6px' }}
                             onBlur={(e) => {
                               if (e.target.value !== (v?.nota ?? '')) {
                                 editar.mutate({
                                   consumoId: x.id,
                                   movimientoId: v?.movimientoId ?? null,
                                   estado: v?.estado,
                                   nota: e.target.value,
                                 });
                               }
                             }} />
                    ) : (v?.nota || <span className="muted">—</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------------------------------------------- 4 detalle resumen */}
      <h2 className="seccion">
        <span className="num">4</span>DETALLE DEL RESUMEN DEL EMISOR
        <span className="muted">— tal como vino en el PDF</span>
      </h2>
      <div className="scroll" style={{ maxHeight: 320 }}>
        <table>
          <thead><tr><th>Fecha</th><th>Comercio</th><th className="num">Importe</th><th>Moneda</th><th>Cuota</th></tr></thead>
          <tbody>
            {r?.consumos.map((x) => (
              <tr key={x.id}>
                <td className="fecha">{x.fecha}</td><td>{x.comercio}</td>
                <td className="num">{pesos(x.importe)}</td><td>{x.moneda}</td><td>{x.cuota ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------------ 5 cargos */}
      <h2 className="seccion">
        <span className="num">5</span>CARGOS E IMPUESTOS DEL RESUMEN
        <span className="muted">— total para registrar el gasto</span>
      </h2>
      <div className="kb">
        <table>
          <thead><tr><th>Concepto</th><th className="num">Importe</th></tr></thead>
          <tbody>
            {(r?.cargos ?? []).length === 0 && (
              <tr><td colSpan={2} className="muted" style={{ padding: 14 }}>Sin cargos en este resumen.</td></tr>
            )}
            {r?.cargos.map((g) => (
              <tr key={g.id}><td>{g.concepto}</td><td className="num">{pesos(g.importe)}</td></tr>
            ))}
            {(r?.cargos ?? []).length > 0 && (
              <tr style={{ fontWeight: 700, background: '#f0f3f9' }}>
                <td>Total de cargos e impuestos</td><td className="num">{pesos(String(totCargos))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --------------------------------------- 6 diferencias a contabilizar */}
      <h2 className="seccion">
        <span className="num">6</span>DIFERENCIAS A CONTABILIZAR
        <span className="muted">— redondeos y conceptos no facturados</span>
      </h2>
      <div className="kb">
        {sec.ajustes.length === 0 ? (
          <div className="pad muted">
            Sin diferencias a contabilizar: todo lo conciliado cuadra exacto.
          </div>
        ) : (
          <table>
            <thead>
              <tr><th>N°</th><th>Comercio</th><th className="num">Importe conciliado</th>
                  <th className="num">Diferencia</th><th>Motivo</th></tr>
            </thead>
            <tbody>
              {sec.ajustes.map((a) => (
                <tr key={a.nroTilde + a.comercio}>
                  <td><span className="cn">{a.nroTilde}</span></td>
                  <td>{a.comercio}</td>
                  <td className="num">{pesos(String(a.importe))}</td>
                  <td className="num">{pesos(String(a.diferencia))}</td>
                  <td className="muted">{a.nota ?? '—'}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, background: '#f0f3f9' }}>
                <td colSpan={3}>Total del ajuste a registrar</td>
                <td className="num">{pesos(String(sec.totalAjustes))}</td><td />
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* --------------------------------------------------- 7 arrastre */}
      <h2 className="seccion">
        <span className="num">7</span>YA CONTABILIZADOS, TODAVÍA NO EN EL RESUMEN
        <span className="muted">— se concilian en resúmenes futuros</span>
      </h2>
      <div className="kb">
        <div className="pad muted" style={{ paddingBottom: 0 }}>
          Asientos del mayor al cierre que <b>no aparecen en este resumen</b>. Al cerrar la
          conciliación quedan guardados como pendientes y se usan como candidatos en el
          resumen siguiente.
        </div>
        {sec.arrastre.length === 0 ? (
          <div className="pad muted">— ninguno —</div>
        ) : (
          <table>
            <thead><tr><th className="fecha">Fecha</th><th>Proveedor / Concepto</th><th className="num">Importe</th></tr></thead>
            <tbody>
              {sec.arrastre.map((a) => (
                <tr key={a.movimientoId}>
                  <td className="fecha">{a.fecha}</td>
                  <td>{a.proveedor}</td>
                  <td className="num">{pesos(String(a.importe))}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, background: '#f0f3f9' }}>
                <td colSpan={2}>Total que se arrastra</td>
                <td className="num">{pesos(String(sec.totalArrastre))}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------- 8 incidencia */}
      <h2 className="seccion">
        <span className="num">8</span>INCIDENCIA EN MESES FUTUROS
        <span className="muted">— compras en cuotas</span>
      </h2>
      <div className="kb">
        {sec.incidencia.length === 0 ? (
          <div className="pad muted">— sin cuotas en este resumen —</div>
        ) : (
          <table>
            <thead><tr><th>N°</th><th className="fecha">Fecha</th><th>Comercio</th><th>Cuota</th><th className="num">Importe</th></tr></thead>
            <tbody>
              {sec.incidencia.map((x, i) => (
                <tr key={i}>
                  <td>{x.nroTilde ? <span className="cn">{x.nroTilde}</span> : <span className="muted">—</span>}</td>
                  <td className="fecha">{x.fecha}</td>
                  <td>{x.comercio}</td>
                  <td><span className="tag info">{x.cuota}</span></td>
                  <td className="num">{pesos(String(x.importe))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------------- 9 mayor */}
      {/* El mayor va ultimo: en el original era una PESTAÑA aparte, no una
          seccion del papel de trabajo. Queda como referencia. */}
      <h2 className="seccion">
        <span className="num">9</span>MAYOR DE LA TARJETA
        <span className="muted">— los movimientos registrados en la contabilidad</span>
      </h2>
      <div className="scroll" style={{ maxHeight: 320 }}>
        <table>
          <thead><tr><th>N°</th><th>Fecha</th><th>Proveedor / Concepto</th><th className="num">Importe</th><th>Tipo</th></tr></thead>
          <tbody>
            {c.movimientos.map((m) => {
              const v = c.vinculos.find((x) => x.movimientoId === m.id);
              return (
                <tr key={m.id}>
                  <td>{v ? <span className="cn">{v.nroTilde}</span> : <span className="muted">—</span>}</td>
                  <td className="fecha">{m.fecha}</td>
                  <td>{m.proveedor ?? m.concepto}</td>
                  <td className="num">{pesos(m.importe)}</td>
                  <td className="muted">{m.tipo ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
